import api from '@/utils/api'
import type {
  QuestionBank,
  Question,
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  CaseMaterialBlock,
  CaseMaterialSummary
} from '@/types'

// 题库相关接口
export interface CreateQuestionBankRequest {
  domain: string
  subdomain: string
  subdomainCode: string
  description?: string
  isActive?: boolean
}

export interface UpdateQuestionBankRequest {
  domain?: string
  subdomain?: string
  subdomainCode?: string
  description?: string
  isActive?: boolean
}

export interface CreateQuestionRequest {
  questionBankId: number
  type: 'singleChoice' | 'multipleChoice' | 'trueFalse' | 'fillBlank' | 'essay' | 'calculation'
  chapter: string
  question: string
  options?: Record<string, string>
  answer: string
  explanation?: string
  difficulty?: number
  isActive?: boolean
  sortOrder?: number
}

export interface ImportQuestionBankRequest {
  questionBankData: {
    domain: string
    subdomain: string
    subDomainCode: string
    title?: string
    quiz?: Array<{
      type: string
      chapter: string
      question: string
      options?: Record<string, string>
      answer: string | string[] | boolean
      explanation?: string
      difficulty?: number
    }>
    cases?: Array<{
      title: string
      chapter?: string
      blocks: CaseMaterialBlock[]
      subQuestions: Array<{
        type: string
        chapter?: string
        question: string
        options?: Record<string, string>
        answer: string | string[] | boolean
        explanation?: string
        difficulty?: number
      }>
    }>
    images?: Record<string, string>
  }
  overwrite: boolean
}

export interface CaseSubQuestionRequest {
  type: 'singleChoice' | 'multipleChoice' | 'trueFalse' | 'fillBlank' | 'essay' | 'calculation'
  chapter?: string
  question: string
  options?: Record<string, string>
  answer: string
  explanation?: string
  difficulty?: number
  isActive?: boolean
}

export interface CreateCaseMaterialRequest {
  title: string
  blocks: CaseMaterialBlock[]
  subQuestions: CaseSubQuestionRequest[]
  chapter?: string
  isActive?: boolean
  sortOrder?: number
}

export interface UpdateCaseMaterialRequest {
  title?: string
  blocks?: CaseMaterialBlock[]
  subQuestions?: CaseSubQuestionRequest[]
  isActive?: boolean
  sortOrder?: number
}

/** 案例题（含子题完整字段，管理端编辑用） */
export interface CaseMaterialDetail extends CaseMaterialSummary {
  questionBankId: number
  isActive: boolean
  sortOrder: number
  questions: Array<Question & { caseSortOrder?: number }>
}

export interface UploadedQuestionImage {
  filename: string
  url: string
}

export interface QuestionBankStats {
  questionBank: QuestionBank
  totalQuestions: number
  questionTypes: Array<{
    type: string
    count: number
  }>
}

export const questionBanksApi = {
  // 题库管理
  async getQuestionBanks(params?: PaginationParams): Promise<PaginatedResponse<QuestionBank>> {
    const response = await api.get('/question-banks', { params })
    return response.data
  },

  async getQuestionBank(id: number): Promise<QuestionBank> {
    const response = await api.get(`/question-banks/${id}`)
    return response.data
  },

  async createQuestionBank(data: CreateQuestionBankRequest): Promise<QuestionBank> {
    const response = await api.post('/question-banks', data)
    return response.data
  },

  async updateQuestionBank(id: number, data: UpdateQuestionBankRequest): Promise<QuestionBank> {
    const response = await api.patch(`/question-banks/${id}`, data)
    return response.data
  },

  async deleteQuestionBank(id: number): Promise<void> {
    await api.delete(`/question-banks/${id}`)
  },

  // 题目管理
  async getQuestionsByBank(bankId: number): Promise<Question[]> {
    const response = await api.get(`/question-banks/${bankId}/questions`)
    return response.data
  },

  async createQuestion(bankId: number, data: any): Promise<Question> {
    const response = await api.post(`/question-banks/${bankId}/questions`, data)
    return response.data
  },

  async getQuestion(id: number): Promise<Question> {
    const response = await api.get(`/question-banks/questions/${id}`)
    return response.data
  },

  async updateQuestion(id: number, data: any): Promise<Question> {
    const response = await api.patch(`/question-banks/questions/${id}`, data)
    return response.data
  },

  async deleteQuestion(id: number): Promise<void> {
    await api.delete(`/question-banks/questions/${id}`)
  },

  // 题库导入
  async importQuestionBank(data: ImportQuestionBankRequest): Promise<QuestionBank> {
    const response = await api.post('/question-banks/import', data)
    return response.data
  },

  async importQuestionBankFromFile(file: File, overwrite: boolean): Promise<QuestionBank> {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await api.post('/question-banks/import/file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: { overwrite }
    })
    return response.data
  },

  async importQuestionBankFromText(data: {
    domain: string
    subdomain: string
    subDomainCode: string
    questions: Array<{
      type: string
      chapter: string
      question: string
      options?: Record<string, string>
      answer: string | string[]
      explanation?: string
    }>
  }, overwrite: boolean): Promise<QuestionBank> {
    const response = await api.post('/question-banks/import/text', {
      ...data,
      overwrite
    })
    return response.data
  },

  // 统计信息
  async getQuestionBankStats(id: number): Promise<QuestionBankStats> {
    const response = await api.get(`/question-banks/${id}/stats`)
    return response.data
  },

  // 检查题库可用性
  async checkQuestionBankAvailability(id: number): Promise<{
    available: boolean;
    totalQuestions: number;
    availableQuestions: number;
  }> {
    const response = await api.get(`/question-banks/${id}/availability`)
    return response.data
  },

  // 获取题库章节列表
  async getChapters(id: number): Promise<string[]> {
    const response = await api.get(`/question-banks/${id}/chapters`)
    return response.data
  },

  async addQuestionsToBank(id: number, questions: Array<{
    type: string;
    chapter: string;
    question: string;
    options?: Record<string, string>;
    answer: string | string[];
    explanation?: string;
  }>): Promise<Question[]> {
    const response = await api.post(`/question-banks/${id}/add-questions`, { questions })
    return response.data
  },

  // 案例题管理（仅管理员）
  async getCaseMaterials(bankId: number): Promise<CaseMaterialDetail[]> {
    const response = await api.get(`/question-banks/${bankId}/cases`)
    return response.data
  },

  async getCaseMaterial(id: number): Promise<CaseMaterialDetail> {
    const response = await api.get(`/question-banks/cases/${id}`)
    return response.data
  },

  async createCaseMaterial(bankId: number, data: CreateCaseMaterialRequest): Promise<CaseMaterialDetail> {
    const response = await api.post(`/question-banks/${bankId}/cases`, data)
    return response.data
  },

  async updateCaseMaterial(id: number, data: UpdateCaseMaterialRequest): Promise<CaseMaterialDetail> {
    const response = await api.patch(`/question-banks/cases/${id}`, data)
    return response.data
  },

  async deleteCaseMaterial(id: number): Promise<void> {
    await api.delete(`/question-banks/cases/${id}`)
  }
}